import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import { Size } from 'src/components/ProductDetail/ProductSizes';
import { useFormApi } from 'src/core/forms';
import { useFieldValue } from 'src/core/forms';
import { AlternateStock } from 'src/components/ProductDetail/AlternateStock/AlternateStock';
import { CallForStockAvailability } from 'src/components/ProductDetail/CallForStockAvailability';
import { Button } from 'src/components/Button/Button';

import { isPositiveInteger, isPositiveIntegerOverZero, QtySelect } from '../Forms/QtySelect';
import { Svg } from '../Svg/Svg';
import { StockStatus } from '../../types/global-types';

import classes from './ProductSizes.scss';
type Props = {
    field: string;
    size: Size;
    notify?: (size: Size) => void;
    classNames: string;
    initialValue?: string;
    isBundle?: boolean;
    sku?: string;
    color?: string;
    isNewProductQtySelect?: boolean;
    selectCorrectShippingMethods?: (sku: string, value: number) => void;
    isNextDayDelivery: boolean;
};

enum Quantity {
    Minimum = 0,
    Maximum = 100,
}

export const ProductSize: React.FunctionComponent<Props> = ({
    classNames,
    field,
    isBundle,
    notify,
    size,
    initialValue,
    sku,
    color,
    isNewProductQtySelect,
    selectCorrectShippingMethods,
    isNextDayDelivery,
}) => {
    const { title, stock, id, simpleProductSku } = size;
    const api = useFormApi();
    const value = useFieldValue(field);
    const isOptionPicked = value > 0;
    const isSelected = (isPositiveInteger(value) && parseInt(value) > 0) || false;
    const ref = useRef<HTMLDivElement | null>(null);
    const onClick = useCallback(
        (e) => {
            const qty = `[data-test-id="QtySelect_${id}"]`;
            const qtyBox = ref.current?.querySelector(qty);
            const curr = api.getValue(field);

            if (isPositiveIntegerOverZero(curr)) {
                if (!qtyBox?.contains(e.target)) {
                    api.setValue(field, '0');
                    selectCorrectShippingMethods?.(simpleProductSku!, 0);
                }
            } else {
                api.setValue(field, '1');
                selectCorrectShippingMethods?.(simpleProductSku!, 1);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [api, field, id],
    );

    const qty = size.maxQty ?? undefined;
    const message = qty ? (qty < Quantity.Maximum ? `${qty} Available` : `100+ Available`) : 'Loading';
    const showAlternateStock = stock === StockStatus.OutOfStock;

    if (!isBundle && isNewProductQtySelect && (stock === StockStatus.OutOfStock || (qty !== undefined && qty <= 0)))
        return (
            <li key={id} className={classes.outOfStockItem}>
                <div className={classes.outOfStockTextContainer}>
                    <span className={classes.outOfStockTitle}>{title}</span>
                    <span className={classes.outOfStockSubtitle}>Out of stock</span>
                </div>
                <Button variant="reset" className={classes.outOfStockButton} onClick={() => notify && notify(size)}>
                    Notify me when in stock
                </Button>
            </li>
        );

    return (
        <li
            key={id}
            data-active={String(isSelected)}
            className={classnames(classNames, {
                [classes.newProductSizesItem]: !isBundle && isNewProductQtySelect,
                [classes.selectedProductSizesItem]: !isBundle && isNewProductQtySelect && isOptionPicked,
            })}
            data-test-id="ProductSize"
        >
            <div
                className={classnames(classes.wrapper, {
                    [classes.sizeDetail]: isBundle || !isNewProductQtySelect,
                })}
            >
                <div
                    onClick={onClick}
                    ref={ref}
                    className={classnames({
                        [classes.productSizeInfo]: !isBundle && !isNewProductQtySelect,
                        [classes.newProductSizeInfo]: !isBundle && isNewProductQtySelect,
                        [classes.selectedProductSizeInfo]: !isBundle && isNewProductQtySelect && isOptionPicked,
                        [classes.bundleSizeInfo]: isBundle,
                    })}
                    data-stock-status={size.stock}
                >
                    {isBundle && (
                        <>
                            {isSelected && (
                                <Svg
                                    name={isBundle ? 'checkbox-active-old' : 'checkbox-active'}
                                    data-testid={isBundle ? 'checkbox-active-old' : 'checkbox-active'}
                                />
                            )}
                            {!isSelected && (
                                <Svg
                                    name={isBundle ? 'checkbox-empty-old' : 'checkbox-empty'}
                                    data-testid={isBundle ? 'checkbox-empty-old' : 'checkbox-empty'}
                                />
                            )}
                        </>
                    )}
                    {title}
                    {isNextDayDelivery && (
                        <img
                            className={classes.thunderbolt}
                            src={
                                isSelected
                                    ? '../../../static/icons/thunderbolt-white.svg'
                                    : '../../../static/icons/thunderbolt-blue.svg'
                            }
                            alt=""
                        />
                    )}
                </div>
                <div
                    className={classnames({
                        [classes.reverseOrderContainer]: !isBundle && isNewProductQtySelect,
                        [classes.standardOrderContainer]: isBundle || !isNewProductQtySelect,
                    })}
                >
                    {Boolean(!size?.hideStockSource) && (
                        <div
                            className={classnames({
                                [classes.productStockInfoContainer]: !isBundle && !isNewProductQtySelect,
                                [classes.newProductStockInfoContainer]: !isBundle && isNewProductQtySelect,
                                [classes.bundleStockInfo]: isBundle,
                            })}
                        >
                            {stock === StockStatus.OutOfStock && notify && (
                                <CallForStockAvailability notify={notify} size={size} />
                            )}
                            {(stock === StockStatus.InStock || stock === StockStatus.LowStock) && (
                                <div
                                    className={classnames({
                                        [classes.productStockInfo]: !isBundle && !isNewProductQtySelect,
                                        [classes.newProductStockInfo]: !isBundle && isNewProductQtySelect,
                                    })}
                                >
                                    <small
                                        className={classnames({
                                            [classes.sizesStock]: !isBundle && !isNewProductQtySelect,
                                            [classes.newProductSizesStockTitle]: !isBundle && isNewProductQtySelect,
                                            [classes.bundleSizesStock]: isBundle,
                                        })}
                                    >
                                        In Stock
                                    </small>
                                    <small
                                        className={classnames({
                                            [classes.sizesStock]: !isBundle && !isNewProductQtySelect,
                                            [classes.newProductSizesStockMsg]: !isBundle && isNewProductQtySelect,
                                            [classes.bundleSizesStock]: isBundle,
                                        })}
                                    >
                                        {message}
                                    </small>
                                </div>
                            )}
                        </div>
                    )}
                    {stock !== StockStatus.OutOfStock && size.maxQty === null && (
                        <QtySelect
                            id={String(id)}
                            field={field}
                            maxValue={0}
                            isBundle={isBundle}
                            isOptionPicked={isOptionPicked}
                            isNewProductQtySelect={isNewProductQtySelect}
                            selectCorrectShippingMethods={selectCorrectShippingMethods}
                            simpleProductSku={simpleProductSku}
                        />
                    )}
                    {stock !== StockStatus.OutOfStock && size.maxQty && (
                        <QtySelect
                            id={String(id)}
                            field={field}
                            maxValue={size.maxQty}
                            initialValue={initialValue}
                            isBundle={isBundle}
                            isOptionPicked={isOptionPicked}
                            isNewProductQtySelect={isNewProductQtySelect}
                            selectCorrectShippingMethods={selectCorrectShippingMethods}
                            simpleProductSku={simpleProductSku}
                        />
                    )}
                </div>
            </div>

            {isBundle && showAlternateStock && <AlternateStock sku={sku} color={color} />}
        </li>
    );
};
